import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const HeaderContainer = styled.header<{ $isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  background: ${(props) =>
    props.$isScrolled ? "rgba(255, 255, 255, 0.95)" : "transparent"};
  backdrop-filter: ${(props) => (props.$isScrolled ? "blur(10px)" : "none")};
  box-shadow: ${(props) =>
    props.$isScrolled ? "0 2px 20px rgba(0, 0, 0, 0.1)" : "none"};
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 1rem;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Logo = styled.a`
  position: relative;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
  transition: color 0.3s ease;
  text-shadow: none;

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
  }

  &:hover {
    color: #667eea;

    &::after {
      width: 100%;
    }
  }
`;

const NavList = styled.ul<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 4rem 2rem 3rem;
    gap: 1.2rem;
    opacity: ${(props) => (props.$isOpen ? '1' : '0')};
    visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
    transform: ${(props) =>
      props.$isOpen ? "translateY(0)" : "translateY(-100%)"};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 9999;
    overflow-y: auto;
  }
`;

const NavItem = styled.li`
  a {
    position: relative;
    font-weight: 500;
    color: #2c3e50;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-decoration: none;
    display: block;

    &::after {
      content: "";
      position: absolute;
      bottom: -5px;
      left: 50%;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, #667eea, #764ba2);
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }

    &:hover {
      color: #667eea;
      transform: translateY(-2px);
    }

    &:hover::after {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 280px;
    
    a {
      font-size: 1.1rem;
      font-weight: 600;
      padding: 1rem 2rem;
      text-align: center;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.15);
      border: 1px solid rgba(255, 255, 255, 0.2);
      margin: 0.2rem 0;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: white;
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      
      &::after {
        display: none;
      }
      
      &:hover, &:focus {
        background: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.4);
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        outline: none;
      }
      
      &:active {
        transform: translateY(-1px) scale(1.01);
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      }
    }
  }
`;

const MobileMenuButton = styled.button<{ $isOpen: boolean }>`
  display: none;
  background: none;
  border: none;
  color: #2c3e50;
  font-size: 1.8rem;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
  transition: all 0.3s ease;
  opacity: ${(props) => (props.$isOpen ? '0' : '1')};
  visibility: ${(props) => (props.$isOpen ? 'hidden' : 'visible')};
  transform: ${(props) => (props.$isOpen ? 'scale(0.8)' : 'scale(1)')};

  &:hover {
    color: #667eea;
    transform: ${(props) => (props.$isOpen ? 'scale(0.8)' : 'scale(1.1)')};
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

// Bouton de fermeture du menu mobile
const MobileMenuClose = styled.button`
  display: none;
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1.8rem;
  cursor: pointer;
  z-index: 10000;
  padding: 0.8rem;
  line-height: 1;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.4);
    transform: rotate(90deg) scale(1.05);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: rotate(90deg) scale(0.95);
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

// Wrapper pour le LanguageSwitcher desktop
const DesktopLanguageSwitcher = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

// Wrapper pour le LanguageSwitcher mobile
const MobileLanguageSwitcher = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${(props) => (props.$isOpen ? 'block' : 'none')};
    position: fixed;
    bottom: 2rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10001;
    opacity: ${(props) => (props.$isOpen ? '1' : '0')};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    padding: 0.5rem;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }
`;

const Header: React.FC = () => {
  const { t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMobileMenuOpen && !target.closest('[data-mobile-menu]')) {
        setIsMobileMenuOpen(false);
      }
    };

    // Gérer le scroll du body quand le menu est ouvert/fermé
    if (isMobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.dataset.scrollY = scrollY.toString();
    } else {
      const scrollY = document.body.dataset.scrollY;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      delete document.body.dataset.scrollY;
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY));
      }
    }

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("click", handleClickOutside);
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("click", handleClickOutside);
      // Nettoyer les styles du body au démontage
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      delete document.body.dataset.scrollY;
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    // Fermer d'abord le menu mobile
    setIsMobileMenuOpen(false);
    
    // Attendre que les styles du body soient restaurés avant de scroller
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        const offsetTop = section.offsetTop - 80;
        window.scrollTo({
          top: offsetTop,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  return (
    <HeaderContainer $isScrolled={isScrolled}>
      <Nav>
        <Logo href="#home">Tomm JOBIT</Logo>

        <NavRight data-mobile-menu>
          <MobileMenuButton
            $isOpen={isMobileMenuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            aria-label="Toggle mobile menu"
          >
            ☰
          </MobileMenuButton>

          <NavList $isOpen={isMobileMenuOpen} data-mobile-menu>
            <MobileMenuClose
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileMenuOpen(false);
              }}
              aria-label="Close mobile menu"
            >
              ×
            </MobileMenuClose>
            
            <NavItem>
              <a
                href="#home"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToSection("home");
                }}
              >
                {t('nav.home')}
              </a>
            </NavItem>
            <NavItem>
              <a
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToSection("about");
                }}
              >
                {t('nav.about')}
              </a>
            </NavItem>
            <NavItem>
              <a
                href="#projects"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToSection("projects");
                }}
              >
                {t('nav.projects')}
              </a>
            </NavItem>
            <NavItem>
              <a
                href="#experience"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToSection("experience");
                }}
              >
                {t('nav.experience')}
              </a>
            </NavItem>
            <NavItem>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  scrollToSection("contact");
                }}
              >
                {t('nav.contact')}
              </a>
            </NavItem>
          </NavList>
          
          <MobileLanguageSwitcher $isOpen={isMobileMenuOpen}>
            <LanguageSwitcher />
          </MobileLanguageSwitcher>
          
          <DesktopLanguageSwitcher>
            <LanguageSwitcher />
          </DesktopLanguageSwitcher>
        </NavRight>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
